import React, { useState, useEffect } from 'react';
import { salesAPI, settingsAPI } from '../services/api';
import Swal from 'sweetalert2';

function Sales() {
  const [sales, setSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [settings, setSettings] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchSales();
    fetchSettings();
  }, []);

  const fetchSales = async () => {
    try {
      const response = await salesAPI.getAll();
      setSales(response.data);
    } catch (error) {
      console.error('Error fetching sales:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const viewDetails = async (saleId) => {
    try {
      const response = await salesAPI.getById(saleId);
      setSelectedSale(response.data);
      setShowDetails(true);
    } catch (error) {
      console.error('Error fetching sale details:', error);
    }
  };

  // Convert image to base64
  const imageToBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to base64:', error);
      return null;
    }
  };

  const printReceipt = async (sale) => {
    const printWindow = window.open('', '_blank');

    // Convert logo to base64 if it exists
    let logoBase64 = null;
    if (settings?.store_logo) {
      const logoUrl = `${window.location.origin}${settings.store_logo}`;
      logoBase64 = await imageToBase64(logoUrl);
    }

    const receiptHTML = generateReceiptHTML(sale, logoBase64);

    printWindow.document.write(receiptHTML);
    printWindow.document.close();

    // Wait for content to load, then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const handleCancelSale = async (sale) => {
    // Show confirmation with sale details
    const result = await Swal.fire({
      title: 'ยกเลิกการขาย',
      html: `
        <div class="text-left mb-4">
          <p class="mb-2"><strong>เลขที่ใบเสร็จ:</strong> ${sale.sale_number}</p>
          <p class="mb-2"><strong>ยอดรวม:</strong> ฿${sale.total_amount.toFixed(2)}</p>
          <p class="mb-2"><strong>ลูกค้า:</strong> ${sale.customer_name}</p>
          <p class="mb-4"><strong>พนักงาน:</strong> ${sale.cashier_name}</p>
          <p class="text-red-600 font-bold">⚠️ การยกเลิกจะคืนสต็อกสินค้ากลับเข้าระบบ</p>
        </div>
        <textarea
          id="cancellation_reason"
          class="swal2-input"
          placeholder="กรุณาระบุเหตุผลในการยกเลิก (บังคับ)"
          style="width: 90%; height: 60px; margin: 10px auto; display: block; resize: none;"
        ></textarea>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ยืนยันยกเลิก',
      cancelButtonText: 'ไม่ยกเลิก',
      width: '500px',
      customClass: {
        popup: 'swal2-overflow-visible',
        htmlContainer: 'swal2-html-container-custom'
      },
      preConfirm: () => {
        const reason = document.getElementById('cancellation_reason').value;
        if (!reason || reason.trim() === '') {
          Swal.showValidationMessage('กรุณาระบุเหตุผลในการยกเลิก');
          return false;
        }
        return { cancellation_reason: reason };
      }
    });

    if (result.isConfirmed) {
      try {
        await salesAPI.cancel(sale.id, result.value);

        Swal.fire({
          icon: 'success',
          title: 'ยกเลิกการขายสำเร็จ',
          text: 'สต็อกสินค้าถูกคืนเข้าระบบเรียบร้อยแล้ว',
          confirmButtonColor: '#0ea5e9'
        });

        fetchSales(); // Refresh list
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'ยกเลิกไม่สำเร็จ',
          text: error.response?.data?.error || 'เกิดข้อผิดพลาด',
          confirmButtonColor: '#0ea5e9'
        });
      }
    }
  };

  const generateReceiptHTML = (sale, logoBase64 = null) => {
    const storeName = settings?.store_name || 'ร้านค้า POS';
    const storeAddress = settings?.store_address || '';
    const storePhone = settings?.store_phone || '';
    const taxId = settings?.tax_id || '';

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>ใบเสร็จ ${sale.sale_number}</title>
  <style>
    @media print {
      @page {
        size: 80mm auto;
        margin: 5mm;
      }
      body {
        margin: 0;
        padding: 0;
      }
    }
    body {
      font-family: 'Sarabun', 'Arial', sans-serif;
      font-size: 12px;
      width: 80mm;
      margin: 0 auto;
      padding: 10px;
    }
    .header {
      text-align: center;
      margin-bottom: 10px;
      border-bottom: 2px dashed #000;
      padding-bottom: 10px;
    }
    .store-logo {
      max-width: 25mm;
      max-height: 25mm;
      margin: 0 auto 5px auto;
      display: block;
    }
    .store-name {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 5px;
    }
    .store-info {
      font-size: 10px;
      margin: 2px 0;
    }
    .receipt-info {
      margin: 10px 0;
      font-size: 11px;
    }
    .receipt-info div {
      margin: 3px 0;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 10px 0;
    }
    th {
      border-top: 1px dashed #000;
      border-bottom: 1px dashed #000;
      padding: 5px 0;
      text-align: left;
      font-size: 11px;
    }
    td {
      padding: 5px 0;
      font-size: 11px;
    }
    .text-right {
      text-align: right;
    }
    .text-center {
      text-align: center;
    }
    .total-section {
      border-top: 1px dashed #000;
      padding-top: 5px;
      margin-top: 5px;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      margin: 3px 0;
    }
    .grand-total {
      font-size: 14px;
      font-weight: bold;
      border-top: 2px solid #000;
      padding-top: 5px;
      margin-top: 5px;
    }
    .footer {
      text-align: center;
      margin-top: 15px;
      padding-top: 10px;
      border-top: 2px dashed #000;
      font-size: 10px;
    }
  </style>
</head>
<body>
  <div class="header">
    ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" class="store-logo">` : ''}
    <div class="store-name">${storeName}</div>
    ${storeAddress ? `<div class="store-info">${storeAddress}</div>` : ''}
    ${storePhone ? `<div class="store-info">โทร: ${storePhone}</div>` : ''}
    ${taxId ? `<div class="store-info">เลขประจำตัวผู้เสียภาษี: ${taxId}</div>` : ''}
  </div>

  <div class="receipt-info">
    <div><strong>เลขที่ใบเสร็จ:</strong> ${sale.sale_number}</div>
    <div><strong>วันที่:</strong> ${new Date(sale.sale_date).toLocaleString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })}</div>
    <div><strong>ลูกค้า:</strong> ${sale.customer_name}</div>
    <div><strong>พนักงาน:</strong> ${sale.cashier_name}</div>
    <div><strong>ชำระโดย:</strong> ${
      sale.payment_method === 'cash' ? 'เงินสด' :
      sale.payment_method === 'transfer' ? 'โอนเงิน' :
      sale.payment_method === 'card' ? 'บัตรเครดิต/เดบิต' :
      sale.payment_method
    }</div>
  </div>

  <table>
    <thead>
      <tr>
        <th>สินค้า</th>
        <th class="text-center">จำนวน</th>
        <th class="text-right">ราคา</th>
        <th class="text-right">รวม</th>
      </tr>
    </thead>
    <tbody>
      ${sale.items.map(item => `
        <tr>
          <td>${item.product_name}</td>
          <td class="text-center">${item.quantity}</td>
          <td class="text-right">฿${parseFloat(item.unit_price).toFixed(2)}</td>
          <td class="text-right">฿${parseFloat(item.subtotal).toFixed(2)}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <div class="total-section">
    <div class="total-row">
      <span>ยอดรวม:</span>
      <span>฿${parseFloat(sale.subtotal).toFixed(2)}</span>
    </div>
    ${(() => {
      // Calculate breakdown of discounts
      const totalDiscount = parseFloat(sale.discount_amount) || 0;
      const pointsDiscount = parseFloat(sale.points_discount) || 0;

      // Calculate promotion discount from items
      const promotionDiscount = sale.items.reduce((sum, item) => {
        return sum + (parseFloat(item.discount_amount) || 0);
      }, 0);

      // Other discount = total - points - promotion
      const otherDiscount = totalDiscount - pointsDiscount - promotionDiscount;

      let discountHTML = '';

      if (promotionDiscount > 0) {
        discountHTML += `
    <div class="total-row">
      <span>ส่วนลดโปรโมชั่น:</span>
      <span>-฿${promotionDiscount.toFixed(2)}</span>
    </div>`;
      }

      if (pointsDiscount > 0) {
        const pointsUsed = sale.points_used || 0;
        discountHTML += `
    <div class="total-row">
      <span>ส่วนลดจากคะแนน (${pointsUsed} คะแนน):</span>
      <span>-฿${pointsDiscount.toFixed(2)}</span>
    </div>`;
      }

      if (otherDiscount > 0) {
        discountHTML += `
    <div class="total-row">
      <span>ส่วนลดอื่นๆ:</span>
      <span>-฿${otherDiscount.toFixed(2)}</span>
    </div>`;
      }

      return discountHTML;
    })()}
    <div class="total-row grand-total">
      <span>ยอดสุทธิ:</span>
      <span>฿${parseFloat(sale.total_amount).toFixed(2)}</span>
    </div>
    ${sale.payment_method === 'cash' ? `
    <div class="total-row">
      <span>รับเงิน:</span>
      <span>฿${parseFloat(sale.paid_amount).toFixed(2)}</span>
    </div>
    <div class="total-row">
      <span>เงินทอน:</span>
      <span>฿${parseFloat(sale.change_amount).toFixed(2)}</span>
    </div>
    ` : ''}
  </div>

  ${sale.member_name ? `
  <div style="border-top: 2px dashed #000; padding-top: 10px; margin-top: 10px; font-size: 11px;">
    <div style="font-weight: bold; margin-bottom: 5px;">⭐ คะแนนสะสม</div>
    ${(() => {
      const earnedPoints = Math.floor(parseFloat(sale.total_amount) / 100);
      const usedPoints = sale.points_used || 0;
      const memberPoints = sale.member_points || 0;

      return `
    <div style="display: flex; justify-content: space-between; margin: 3px 0;">
      <span>คะแนนที่ได้รับ:</span>
      <span style="color: #16a34a; font-weight: bold;">+${earnedPoints} คะแนน</span>
    </div>
    ${usedPoints > 0 ? `
    <div style="display: flex; justify-content: space-between; margin: 3px 0;">
      <span>คะแนนที่ใช้:</span>
      <span style="color: #dc2626; font-weight: bold;">-${usedPoints} คะแนน</span>
    </div>
    ` : ''}
    <div style="display: flex; justify-content: space-between; margin: 3px 0; padding-top: 5px; border-top: 1px dashed #666;">
      <span style="font-weight: bold;">คะแนนคงเหลือ:</span>
      <span style="color: #0ea5e9; font-weight: bold;">${memberPoints.toLocaleString()} คะแนน</span>
    </div>
    ${(() => {
      // Calculate days until January 1st of next year (when points reset)
      const today = new Date();
      const currentYear = today.getFullYear();
      const nextJanuary1 = new Date(currentYear + 1, 0, 1); // January 1st next year
      const daysUntilReset = Math.ceil((nextJanuary1 - today) / (1000 * 60 * 60 * 24));

      // Show warning
      if (daysUntilReset > 0) {
        return `
    <div style="background-color: #fef3c7; border: 1px solid #f59e0b; border-radius: 4px; padding: 5px; margin-top: 5px;">
      <div style="color: #d97706; font-weight: bold; font-size: 10px; text-align: center;">
        ⚠️ คะแนนของคุณจะหมดอายุอีก ${daysUntilReset} วัน<br>
        แล้วระบบจะรีเซ็ทเป็น 0
      </div>
    </div>
        `;
      }
      return '';
    })()}
      `;
    })()}
  </div>
  ` : ''}

  <div class="footer">
    <div>ขอบคุณที่ใช้บริการ</div>
    <div>โปรดตรวจสอบสินค้าก่อนออกจากร้าน</div>
  </div>

  <script>
    window.onload = () => {
      setTimeout(() => {
        window.print();
      }, 250);
    };
  </script>
</body>
</html>
    `;
  };

  // Filter sales based on search term and date range
  const filteredSales = sales.filter(sale => {
    // Text search filter
    const matchesSearch = !searchTerm ||
      sale.sale_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.cashier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.payment_method.toLowerCase().includes(searchTerm.toLowerCase());

    // Date range filter - compare dates only (ignore time)
    const saleDate = new Date(sale.sale_date);
    const saleDateString = `${saleDate.getFullYear()}-${String(saleDate.getMonth() + 1).padStart(2, '0')}-${String(saleDate.getDate()).padStart(2, '0')}`;

    const matchesStartDate = !startDate || saleDateString >= startDate;
    const matchesEndDate = !endDate || saleDateString <= endDate;

    return matchesSearch && matchesStartDate && matchesEndDate;
  });

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-6">ประวัติการขาย</h2>

      {/* Search and Filter Section */}
      <div className="mb-4 space-y-4">
        {/* Text Search */}
        <div>
          <input
            type="text"
            placeholder="ค้นหาเลขที่ใบเสร็จ, ชื่อลูกค้า, พนักงาน, หรือวิธีชำระเงิน..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Date Range Filter */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              วันที่เริ่มต้น
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              วันที่สิ้นสุด
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setSearchTerm('');
              }}
              className="w-full p-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
            >
              ล้างตัวกรอง
            </button>
          </div>
        </div>

        {/* Results Summary */}
        {(searchTerm || startDate || endDate) && (
          <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-sm text-gray-700">
              <span className="font-semibold">พบ {filteredSales.length} รายการ</span>
              <span className="text-gray-600"> จากทั้งหมด {sales.length} รายการ</span>
              {startDate && endDate && (
                <span className="ml-2 text-gray-600">
                  (ระหว่าง {new Date(startDate).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' })} - {new Date(endDate).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' })})
                </span>
              )}
              {startDate && !endDate && (
                <span className="ml-2 text-gray-600">
                  (ตั้งแต่ {new Date(startDate).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' })})
                </span>
              )}
              {!startDate && endDate && (
                <span className="ml-2 text-gray-600">
                  (ถึง {new Date(endDate).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' })})
                </span>
              )}
            </div>
            <div className="text-sm font-semibold text-primary-600">
              ยอดรวม: ฿{filteredSales.reduce((sum, sale) => sum + sale.total_amount, 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">เลขที่ใบเสร็จ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ลูกค้า</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ยอดรวม</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ชำระโดย</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">พนักงาน</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">จัดการ</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredSales.map((sale) => (
              <tr key={sale.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">{sale.sale_number}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {new Date(sale.sale_date).toLocaleString('th-TH', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{sale.customer_name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold">
                  ฿{sale.total_amount.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {sale.payment_method === 'cash' ? 'เงินสด' :
                   sale.payment_method === 'transfer' ? 'โอนเงิน' :
                   sale.payment_method === 'card' ? 'บัตรเครดิต/เดบิต' :
                   sale.payment_method}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{sale.cashier_name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {sale.payment_status === 'cancelled' ? (
                    <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800">ยกเลิกแล้ว</span>
                  ) : (
                    <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">สำเร็จ</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => viewDetails(sale.id)}
                    className="text-primary-600 hover:text-primary-900 mr-3"
                  >
                    ดูรายละเอียด
                  </button>
                  {sale.payment_status !== 'cancelled' && (
                    <button
                      onClick={() => handleCancelSale(sale)}
                      className="text-red-600 hover:text-red-900"
                    >
                      ยกเลิก
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Details Modal */}
      {showDetails && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">รายละเอียดการขาย</h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ปิด
              </button>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2 border-b pb-2 mb-2">
                  <div className="flex items-center justify-between">
                    <span><strong>เลขที่ใบเสร็จ:</strong> <span className="font-mono text-primary-600">{selectedSale.sale_number}</span></span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      selectedSale.payment_status === 'cancelled'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {selectedSale.payment_status === 'cancelled' ? 'ยกเลิกแล้ว' : 'ชำระเงินแล้ว'}
                    </span>
                  </div>
                </div>
                <div><strong>วันที่-เวลา:</strong> {new Date(selectedSale.sale_date).toLocaleString('th-TH', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}</div>
                <div><strong>พนักงาน:</strong> {selectedSale.cashier_name}</div>
                <div><strong>ลูกค้า:</strong> {selectedSale.customer_name || '-'}
                  {selectedSale.member_name && (
                    <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                      สมาชิก
                    </span>
                  )}
                </div>
                <div><strong>ชำระโดย:</strong> <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                  {selectedSale.payment_method === 'cash' ? 'เงินสด' :
                   selectedSale.payment_method === 'transfer' ? 'โอนเงิน' :
                   selectedSale.payment_method === 'card' ? 'บัตรเครดิต/เดบิต' :
                   selectedSale.payment_method}
                </span></div>
                {selectedSale.member_phone && (
                  <div className="col-span-2"><strong>เบอร์โทรสมาชิก:</strong> {selectedSale.member_phone}</div>
                )}
              </div>
              {selectedSale.notes && (
                <div className="mt-3 pt-3 border-t">
                  <strong className="text-xs text-gray-600">หมายเหตุ:</strong>
                  <p className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">{selectedSale.notes}</p>
                </div>
              )}
            </div>

            <h4 className="font-bold mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              รายการสินค้า ({selectedSale.items.length} รายการ)
            </h4>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">สินค้า</th>
                    <th className="px-3 py-2 text-left">บาร์โค้ด</th>
                    <th className="px-3 py-2 text-right">ราคา/หน่วย</th>
                    <th className="px-3 py-2 text-center">จำนวน</th>
                    <th className="px-3 py-2 text-right">ส่วนลด</th>
                    <th className="px-3 py-2 text-right">รวม</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSale.items.map((item, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2 font-medium">{item.product_name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-600">{item.barcode || '-'}</td>
                      <td className="px-3 py-2 text-right">฿{item.unit_price.toFixed(2)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className="px-2 py-1 bg-gray-100 rounded">{item.quantity}</span>
                      </td>
                      <td className="px-3 py-2 text-right text-orange-600">
                        {item.discount_amount > 0 ? `-฿${item.discount_amount.toFixed(2)}` : '-'}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">฿{item.subtotal.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">ยอดรวมสินค้า:</span>
                <span className="font-medium">฿{selectedSale.subtotal.toFixed(2)}</span>
              </div>

              {/* แสดงส่วนลดจากสินค้า (ถ้ามี) */}
              {selectedSale.discount_amount > 0 && selectedSale.points_discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">ส่วนลดสินค้า:</span>
                  <span className="font-medium text-orange-600">-฿{(selectedSale.discount_amount - selectedSale.points_discount).toFixed(2)}</span>
                </div>
              )}

              {/* แสดงส่วนลดจากคะแนน (ถ้ามี) */}
              {selectedSale.points_used > 0 && selectedSale.points_discount > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 -mx-2 px-4">
                  <div className="flex justify-between items-center text-sm mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-yellow-600">⭐</span>
                      <span className="text-gray-700 font-semibold">ใช้คะแนนส่วนลด:</span>
                    </div>
                    <span className="font-bold text-yellow-700">-฿{selectedSale.points_discount.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1 ml-6">
                    <span>ใช้คะแนน</span>
                    <span className="font-bold text-yellow-600">{selectedSale.points_used}</span>
                    <span>คะแนน</span>
                    {settings && (
                      <span className="text-gray-500">
                        ({settings.redemption_points} คะแนน = {settings.redemption_value} บาท)
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* แสดงส่วนลดทั้งหมด (กรณีไม่มีการใช้คะแนน) */}
              {selectedSale.discount_amount > 0 && (!selectedSale.points_used || selectedSale.points_used === 0) && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">ส่วนลดทั้งหมด:</span>
                  <span className="font-medium text-orange-600">-฿{selectedSale.discount_amount.toFixed(2)}</span>
                </div>
              )}

              {selectedSale.tax_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">ภาษี:</span>
                  <span className="font-medium">฿{selectedSale.tax_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t-2 border-gray-300 pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold">
                  <span>ยอดสุทธิ:</span>
                  <span className="text-primary-600">฿{selectedSale.total_amount.toFixed(2)}</span>
                </div>
              </div>
              {selectedSale.payment_method === 'cash' && (
                <>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">รับเงินมา:</span>
                      <span className="font-medium">฿{selectedSale.paid_amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-600">เงินทอน:</span>
                      <span className="font-bold text-green-600 text-lg">฿{selectedSale.change_amount.toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}
              {selectedSale.payment_method === 'transfer' && (
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">ยอดโอน:</span>
                    <span className="font-medium">฿{selectedSale.paid_amount.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Points Earned Section - Only show for member purchases */}
            {selectedSale.member_id && selectedSale.payment_status !== 'cancelled' && settings && (
              <div className="mt-4 p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-yellow-500 text-2xl">⭐</span>
                    <div>
                      <span className="font-bold text-purple-800 text-lg">คะแนนที่ได้รับ</span>
                      <p className="text-xs text-gray-600">จากการซื้อ ฿{selectedSale.total_amount.toFixed(2)}</p>
                      <p className="text-xs text-purple-600">อัตรา: ซื้อ {settings.points_earn_baht || 100} บาท = {settings.points_earn_points || 1} คะแนน</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-purple-600">
                      +{Math.floor((selectedSale.total_amount / (settings.points_earn_baht || 100)) * (settings.points_earn_points || 1))}
                    </div>
                    <div className="text-xs text-purple-700 font-medium">คะแนน</div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => printReceipt(selectedSale)}
              className="w-full mt-6 bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded"
            >
              พิมพ์ใบเสร็จ
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Sales;
